import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Createable from "containers/Select";
import { fetchSubscriptionPlans } from "services/auth";
import { toast } from "react-toastify";
import { launchRazorpay } from "containers/pdf/utils";
import { renewSubscription } from "store/features/authSlice";
import { useDispatch } from "react-redux";
import { sendPaymentFailureEmailToAdmin } from "firebaseConfig";
import { fetchUSDExchangeRate } from "containers/pdf/utils";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "firebaseConfig";

function RenewSubscriptionModal({
  open,
  onClose,
  userData,
  referrals,
  onRenewSuccess,
  uid,
}) {
  const dispatch = useDispatch();
  const [subscriptionType, setSubscriptionType] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usdRate, setUsdRate] = useState(null);
  const [formdata, setFormdata] = useState({
    referenceCode: null,
  });
  const [errorMessage, setErrorMessage] = useState();
  const [referralApplied, setReferralApplied] = useState(false);
  const [referenceDetaihls, setReferenceDetails] = useState(null);

  useEffect(() => {
    const getExchangeRate = async () => {
      try {
        const dollarValue = await fetchUSDExchangeRate();
        setUsdRate(dollarValue);
      } catch (error) {
        console.error("Failed to fetch USD exchange rate:", error);
      }
    };

    getExchangeRate();
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      let subs = [];
      setLoading(true);
      subs = await fetchSubscriptionPlans();
      const formattedSubs = subs.map((plan) => {
        const usdAmount = usdRate
          ? (plan.paymentAmount * usdRate).toFixed(2)
          : "0.00";
        return {
          ...plan,
          label: `${plan.plan} (${plan.months} month${
            plan.months > 1 ? "s" : ""
          }, $${usdAmount} ≈ ₹${plan.paymentAmount})`,
        };
      });
      setSubscriptionType(formattedSubs);
    } catch (error) {
      toast.error("Failed to load subscription plans");
    } finally {
      setLoading(false);
    }
  }, [usdRate]);

  const computedRenewPlanDetails = useMemo(() => {
    if (!subscription) return null;

    const baseAmount = parseFloat(subscription.paymentAmount || 0);
    const gstPercentage = subscription.gstPercentage || 18;

    // GST calculations
    const gstAmount = (baseAmount * gstPercentage) / 100;
    const totalAmount = baseAmount + gstAmount;

    // Referral credits (unredeemed)
    let referAmount = 0;
    referrals?.forEach((item) => {
      if (!item.redeemed) {
        referAmount += item.amount;
      }
    });
    // Ensure we don't deduct more than base
    // referAmount = Math.min(referAmount, baseAmount);
    referAmount = 0;

    // Referral discount (percentage-based)
    const referralPercent = referralApplied
      ? subscription.referralPercent || 5
      : 0;

    const referralDiscount = (baseAmount * referralPercent) / 100;

    // Compute final
    const finalAmount =
      totalAmount - referAmount - referralDiscount <= 0
        ? 1
        : totalAmount - referAmount - referralDiscount;

    return {
      baseAmount,
      gstPercentage,
      gstAmount,
      totalAmount,
      referralDiscount,
      referAmount,
      finalAmount,
      referralPercent,
    };
  }, [subscription, referrals, referralApplied]);

  const validateReferralCode = async (code, currentPlan = subscription) => {
    if (!code || code.trim() === "") {
      setReferralApplied(false);
      setReferenceDetails(null);
      if (currentPlan) {
        setSubscription({
          ...currentPlan,
          referralAmount: 0,
        });
      }

      return false;
    }

    const q = query(
      collection(firestore, "profile"),
      where("code", "==", code.trim())
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      const referredByName = docSnap.data().name;
      const referredByEmail = docSnap.data().email;

      setReferralApplied(true);
      setReferenceDetails({
        ref: docSnap.id,
        name: referredByName,
        email: referredByEmail,
      });
      if (currentPlan) {
        setSubscription({
          ...currentPlan,
          referralAmount: currentPlan.originalReferralAmount ?? 0,
        });
      }

      return true;
    } else {
      setReferralApplied(false);
      setReferenceDetails(null);

      if (currentPlan) {
        setSubscription({
          ...currentPlan,
          referralAmount: 0,
        });
      }

      return false;
    }
  };

  useEffect(() => {
    const validate = async () => {
      if (subscription) {
        await validateReferralCode(formdata.referenceCode);
      }
    };

    validate();
  }, [formdata.referenceCode]);

  useEffect(() => {
    if (open) {
      fetchSubscriptions();
    }
  }, [open, fetchSubscriptions]);

  const handleRenew = async () => {
    let referral = false;
    let referenceDetails = null;
    if (!subscription) {
      toast.info("Please select a subscription plan");
      return;
    }

    if (formdata.referenceCode && formdata.referenceCode.trim() !== "") {
      let q = query(
        collection(firestore, "profile"),
        where("code", "==", formdata.referenceCode.trim())
      );
      let querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
          referral = true;
          let docRef = doc.id;
          referenceDetails = {
            ref: docRef,
            name: doc.data().name,
            amount: computedRenewPlanDetails.referralDiscount,
          };
        });
      } else {
        referenceDetails = null;
        return toast.info("Invalid referral code provided");
      }
    } else {
      referenceDetails = null;
    }

    setLoading(true);
    try {
      let referAmount = computedRenewPlanDetails?.referralDiscount || 0;

      let referralList = referrals?.map((item) => {
        if (!item.redeemed) {
          if (referAmount !== parseFloat(subscription.paymentAmount)) {
            referAmount += item.amount;

            if (referAmount > parseFloat(subscription.paymentAmount)) {
              let amt = {
                amount: referAmount - parseFloat(subscription.paymentAmount),
                ...item,
              };
              referAmount = parseFloat(subscription.paymentAmount);
              return amt;
            }
            return { ...item, redeemed: true };
          }
        }
        return item;
      });

      const paymentInfo = {
        formdata: {
          name: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNumber,
        },
        paymentData: {
          paymentAmount: computedRenewPlanDetails.baseAmount,
          gstAmount: computedRenewPlanDetails.gstAmount,
          totalAmount: computedRenewPlanDetails.totalAmount,
          finalAmount: computedRenewPlanDetails.finalAmount,
          monthOfSubscription: subscription.monthOfSubscription,
          plan: subscription.plan,
        },
        referenceDetails: null,
        referral: true,
        amount: computedRenewPlanDetails.finalAmount * 100,
        referAmount: computedRenewPlanDetails.referralDiscount,
        referralList,
        onPaymentSuccess: async (
          payResponse,
          paymentData,
          referenceDetails
        ) => {
          await dispatch(
            renewSubscription({
              payResponse,
              uid,
              referralList,
              subscription,
              paymentData,
            })
          );
          setLoading(false);
          onRenewSuccess();
        },
        onPaymentFailure: async (error) => {
          setLoading(false);
          const errorMsg =
            typeof error === "string"
              ? error
              : error?.description || "Payment failed. Please try again.";
          toast.error(errorMsg);
          console.error("Payment error:", error);

          try {
            await sendPaymentFailureEmailToAdmin({
              adminName: userData.name,
              adminEmail: userData.email,
              amount: computedRenewPlanDetails.totalAmount,
              errorMessage: errorMsg,
              logo: userData.logo,
            });
          } catch (err) {
            console.error("Failed to send failure email:", err);
          }
        },
      };
      launchRazorpay(paymentInfo);
    } catch (err) {
      toast.error("Failed to renew subscription");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <SuiTypography variant="h5" fontWeight="medium">
          Renew Subscription
        </SuiTypography>
      </DialogTitle>

      <DialogContent dividers>
        <SuiBox mb={2}>
          <SuiInput label="Name" value={userData.name} fullWidth disabled />
        </SuiBox>

        <SuiBox mb={2}>
          <Createable
            options={subscriptionType}
            value={subscription}
            onChange={(e) => setSubscription(e)}
            placeholder="Select new plan"
          />
        </SuiBox>

        <SuiBox mb={2}>
          <SuiInput label="Email" value={userData.email} fullWidth disabled />
        </SuiBox>

        <SuiBox mb={2}>
          <SuiInput
            label="Phone Number"
            value={userData.phoneNumber}
            fullWidth
            disabled
          />
        </SuiBox>

        <SuiBox mb={2}>
          <TextField
            fullWidth
            placeholder="Enter reference code"
            value={formdata?.referenceCode ?? ""}
            error={errorMessage?.referenceCode}
            onChange={(e) => {
              setErrorMessage({
                ...errorMessage,
                referenceCode: false,
              });
              setFormdata({
                ...formdata,
                referenceCode: e.target.value,
              });
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "10px",
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "2px solid #17C1E8",
                },
              },
              "& .MuiInputBase-input": {
                fontFamily: "Outfit",
                padding: "12px 16px",
              },
            }}
          />
        </SuiBox>

        {computedRenewPlanDetails && usdRate && (
          <SuiBox my={2}>
            <SuiTypography variant="button" fontWeight="bold" mb={1}>
              💳 Plan Summary (USD)
            </SuiTypography>

            <SuiTypography variant="body2" mb={0.5}>
              Base Amount: $
              {(computedRenewPlanDetails.baseAmount * usdRate).toFixed(2)}
              <small style={{ color: "#999" }}>
                (≈ ₹{computedRenewPlanDetails.baseAmount.toFixed(2)})
              </small>
            </SuiTypography>

            <SuiTypography variant="body2" mb={0.5}>
              GST ({computedRenewPlanDetails.gstPercentage}%): $
              {(computedRenewPlanDetails.gstAmount * usdRate).toFixed(2)}
              <small style={{ color: "#999" }}>
                (≈ ₹{computedRenewPlanDetails.gstAmount.toFixed(2)})
              </small>
            </SuiTypography>

            <SuiTypography variant="body2" mb={0.5}>
              Total Amount (Incl. GST): $
              {(computedRenewPlanDetails.totalAmount * usdRate).toFixed(2)}
              <small style={{ color: "#999" }}>
                (≈ ₹{computedRenewPlanDetails.totalAmount.toFixed(2)})
              </small>
            </SuiTypography>

            {referralApplied &&
              computedRenewPlanDetails.referralDiscount > 0 && (
                <>
                  <SuiTypography
                    variant="body2"
                    sx={{
                      color: "#4CAF50",
                      fontFamily: "Outfit",
                      mb: 0.5,
                    }}
                  >
                    Referral Discount (
                    {computedRenewPlanDetails.referralPercent
                      ? computedRenewPlanDetails.referralPercent
                      : 5}
                    %) : -$
                    {(
                      computedRenewPlanDetails.referralDiscount * usdRate
                    ).toFixed(2)}
                    <small style={{ color: "#999" }}>
                      (≈ -₹
                      {computedRenewPlanDetails.referralDiscount.toFixed(2)})
                    </small>
                  </SuiTypography>

                  <SuiTypography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#17C1E8", fontFamily: "Outfit" }}
                  >
                    Final Amount: $
                    {(computedRenewPlanDetails.finalAmount * usdRate).toFixed(
                      2
                    )}
                    <small style={{ color: "#999" }}>
                      (≈ ₹{computedRenewPlanDetails.finalAmount.toFixed(2)})
                    </small>
                  </SuiTypography>
                </>
              )}
          </SuiBox>
        )}
      </DialogContent>

      <DialogActions>
        <SuiButton onClick={onClose} color="secondary">
          Cancel
        </SuiButton>
        <SuiButton onClick={handleRenew} color="dark" disabled={loading}>
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Pay & Renew"
          )}
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
}

export default RenewSubscriptionModal;
